import React from "react";
import PropTypes from "prop-types";
import { Button, CircularProgress } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { colors, customShadows, borderRadiusPx } from "../../../../theme";

/**
 * CommonButton Component
 * Centralized, reusable button component adhering to the AutoBET theme palette & elevation tokens.
 * All colors, padding, borders, shadows, and hover transitions are managed centrally.
 *
 * Supported variants:
 * - "primary" / "contained": Solid brand blue fill with hover glow elevation.
 * - "outlined" / "outline" (default): Subtle card border, clean slate text, primary hover glow.
 * - "secondary" / "dark": Solid slate 900 background with dark hover elevation.
 * - "action" / "actionOutlined": Elevated card-style action button with light blue accent.
 * - "refresh" / "toolbar" / "neutralOutlined": Paper background, subtle border, slate 700 text turning brand blue on hover.
 * - "text": Clean transparent button with brand blue hover tint.
 * - "reset" / "cancel" / "ghost" / "secondaryText" / "mutedText": Neutral slate text button for dialog cancel, popup actions, and filter resets.
 * - "danger" / "dangerOutlined": Red alert border and text with red-50 hover tint.
 * - "dangerText" / "dangerGhost": Red transparent text button with red-50 hover tint.
 * - "dangerSolid" / "dangerContained": Solid red background with white text.
 */
const CommonButton = ({
  children,
  label,
  variant = "outlined",
  size = "medium",
  loading = false,
  disabled = false,
  startIcon,
  endIcon,
  onClick,
  fullWidth = false,
  sx = {},
  ...props
}) => {
  // Size-based padding, font sizes & height
  const sizeStyles =
    {
      small: {
        fontSize: "0.78rem",
        px: 2,
        py: 0.75,
        minHeight: "34px",
        borderRadius: borderRadiusPx?.lg || "10px",
      },
      input: {
        fontSize: "0.85rem",
        px: 2.5,
        py: 1,
        minHeight: "40px",
        borderRadius: borderRadiusPx?.xl || "12px",
      },
      medium: {
        fontSize: "0.875rem",
        px: 2.75,
        py: 1.1,
        minHeight: "42px",
        borderRadius: borderRadiusPx?.xl || "12px",
      },
      large: {
        fontSize: "0.95rem",
        px: 3.5,
        py: 1.35,
        minHeight: "48px",
        borderRadius: borderRadiusPx?.xl || "12px",
      },
      hero: {
        fontSize: "0.95rem",
        px: 4.5,
        py: 1.25,
        minHeight: "48px",
        borderRadius: "50px",
        minWidth: "180px",
      },
    }[size] || {
      fontSize: "0.875rem",
      px: 2.75,
      py: 1.1,
      minHeight: "42px",
      borderRadius: borderRadiusPx?.xl || "12px",
    };

  // Variant-specific styling rules
  let variantStyles = {};
  let muiVariant = "outlined";

  switch (variant) {
    case "contained":
    case "primary":
      muiVariant = "contained";
      variantStyles = {
        backgroundColor: colors.primary.main,
        color: colors.primary.contrastText || "#ffffff",
        boxShadow: customShadows.buttonPrimary || "0 4px 14px rgba(37, 99, 235, 0.25)",
        borderRadius: borderRadiusPx?.xl || "12px",
        border: "none",
        "&:hover": {
          backgroundColor: colors.primary.dark,
          boxShadow: customShadows.buttonPrimaryHover || "0 6px 20px rgba(37, 99, 235, 0.35)",
        },
      };
      break;

    case "secondary":
    case "dark":
      muiVariant = "contained";
      variantStyles = {
        backgroundColor: colors.slate[900],
        color: colors.primary.contrastText || "#ffffff",
        boxShadow: customShadows.cardFlat,
        borderRadius: borderRadiusPx?.xl || "12px",
        border: "none",
        "&:hover": {
          backgroundColor: colors.slate[800],
          boxShadow: customShadows.buttonDarkHover,
        },
      };
      break;

    case "action":
    case "actionOutlined":
      muiVariant = "outlined";
      variantStyles = {
        borderColor: colors.dashboard?.cardBorder || colors.slate[200],
        backgroundColor: colors.dashboard?.cardBg || colors.background.paper,
        color: colors.primary.main,
        boxShadow: customShadows.cardFlat,
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          borderColor: colors.blue[300],
          backgroundColor: colors.blue[50],
          boxShadow: customShadows.buttonActionHover,
        },
      };
      break;

    case "refresh":
    case "toolbar":
    case "neutralOutlined":
      muiVariant = "outlined";
      variantStyles = {
        backgroundColor: colors.background.paper || "#ffffff",
        borderColor: colors.dashboard?.cardBorder || colors.slate[200],
        color: colors.slate[700],
        borderRadius: borderRadiusPx?.xl || "12px",
        boxShadow: customShadows.cardFlat,
        "&:hover": {
          borderColor: colors.primary.main,
          backgroundColor: colors.primary.lighter || colors.blue[50],
          color: colors.primary.main,
        },
      };
      break;

    case "text":
      muiVariant = "text";
      variantStyles = {
        color: colors.primary.main,
        backgroundColor: "transparent",
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          backgroundColor: colors.blue[50],
        },
      };
      break;

    case "reset":
    case "cancel":
    case "ghost":
    case "secondaryText":
    case "mutedText":
    case "neutralText":
      muiVariant = "text";
      variantStyles = {
        color: colors.slate[600],
        backgroundColor: "transparent",
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          backgroundColor: colors.slate[100],
          color: colors.slate[900],
        },
        "&.Mui-disabled": {
          color: colors.slate[400],
        },
      };
      break;

    case "dangerText":
    case "dangerGhost":
      muiVariant = "text";
      variantStyles = {
        color: colors.dashboard?.status?.dangerText || colors.error?.main || colors.red[600],
        backgroundColor: "transparent",
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          backgroundColor: colors.red[50] || "#fef2f2",
          color: colors.error?.dark || colors.red[700] || "#dc2626",
        },
        "&.Mui-disabled": {
          color: colors.slate[400],
        },
      };
      break;

    case "dangerSolid":
    case "dangerContained":
      muiVariant = "contained";
      variantStyles = {
        backgroundColor: colors.error?.main || colors.red[600],
        color: "#ffffff",
        borderRadius: borderRadiusPx?.xl || "12px",
        boxShadow: customShadows.cardFlat,
        border: "none",
        "&:hover": {
          backgroundColor: colors.error?.dark || colors.red[700],
          boxShadow: customShadows.buttonDarkHover,
        },
      };
      break;

    case "danger":
    case "dangerOutlined":
      muiVariant = "outlined";
      variantStyles = {
        borderColor: colors.red[200],
        color: colors.red[600],
        backgroundColor: colors.background?.paper || "#ffffff",
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          borderColor: colors.red[400],
          backgroundColor: colors.red[50],
          color: colors.red[700],
        },
      };
      break;

    case "outline":
    case "outlined":
    default:
      muiVariant = "outlined";
      variantStyles = {
        borderColor: colors.dashboard?.cardBorder || colors.slate[200],
        backgroundColor: colors.background?.paper || "#ffffff",
        color: colors.slate[700],
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          borderColor: colors.primary.main,
          backgroundColor: colors.primary.lighter || colors.blue[50],
          color: colors.primary.main,
        },
      };
      break;
  }

  const combinedHover = {
    ...(variantStyles["&:hover"] || {}),
    letterSpacing: "0.02em",
    "& .MuiButton-endIcon": {
      transform: "translateX(4px)",
      color: "inherit",
    },
    "& .MuiButton-startIcon": {
      color: "inherit",
    },
    "& .MuiSvgIcon-root": {
      color: "inherit",
    },
  };

  // Determine active start icon (auto-loading spinner / refresh icon fallback)
  const renderStartIcon = () => {
    if (loading) {
      return (
        <CircularProgress
          size={size === "small" ? 14 : 16}
          color="inherit"
        />
      );
    }
    if (startIcon !== undefined) {
      return startIcon;
    }
    if (variant === "refresh") {
      return <RefreshIcon sx={{ fontSize: size === "small" ? 16 : 18 }} />;
    }
    return null;
  };

  const buttonContent = children ?? label ?? (variant === "refresh" ? "Refresh" : null);

  return (
    <Button
      variant={muiVariant}
      onClick={onClick}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      startIcon={renderStartIcon()}
      endIcon={!loading ? endIcon : null}
      sx={{
        fontWeight: 600,
        textTransform: "none",
        letterSpacing: "0.01em",
        boxSizing: "border-box",
        transition: "all 0.2s ease-in-out",
        "& .MuiButton-startIcon": {
          color: "inherit",
          "& .MuiSvgIcon-root": {
            color: "inherit",
          },
        },
        "& .MuiButton-endIcon": {
          color: "inherit",
          transition: "transform 0.2s ease-in-out",
          "& .MuiSvgIcon-root": {
            color: "inherit",
          },
        },
        "& .MuiSvgIcon-root": {
          color: "inherit",
        },
        ...sizeStyles,
        ...variantStyles,
        "&:hover": combinedHover,
        ...sx,
      }}
      {...props}
    >
      {buttonContent}
    </Button>
  );
};

CommonButton.propTypes = {
  children: PropTypes.node,
  label: PropTypes.node,
  variant: PropTypes.oneOf([
    "outlined",
    "outline",
    "contained",
    "primary",
    "secondary",
    "dark",
    "action",
    "actionOutlined",
    "refresh",
    "toolbar",
    "neutralOutlined",
    "text",
    "reset",
    "cancel",
    "ghost",
    "secondaryText",
    "mutedText",
    "neutralText",
    "danger",
    "dangerOutlined",
    "dangerText",
    "dangerGhost",
    "dangerSolid",
    "dangerContained",
  ]),
  size: PropTypes.oneOf(["small", "input", "medium", "large", "hero"]),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  onClick: PropTypes.func,
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
};

export default CommonButton;
